import { useEffect, useMemo, useState } from "react";
import { format, subMonths, parseISO, eachMonthOfInterval } from "date-fns";
import {
  getAllPromoters, getSalesByMonth, getAllTargets, getTarget, getAllSKUs,
  getIncomeHistoryAll, getIncomeHistoryForPromoter, getSupervisorAssignment,
  getAssignedPromoters, getSalesByMonthForPromoters, getTargetsForPromoters, getIncomeHistoryForPromoters,
} from "../../firebase/services";
import { useAuth } from "../../hooks/useAuth";
import { useLanguage } from "../../hooks/useLanguage";

// Báo cáo bán hàng nhiều tháng: theo promoter (so với target), theo model, và cơ cấu phân khúc.
// Tháng chưa có đơn trong app nhưng có số liệu import (incomeHistory) → dùng số import, đánh dấu "nhập".
// Phạm vi dữ liệu theo vai trò: admin thấy tất cả; supervisor chỉ promoter mình quản lý;
// promoter chỉ số của chính mình (query lọc theo promoterId — đúng với Firestore rules).

// Tải dữ liệu của các tháng trong phạm vi vai trò
const loadScoped = async (role, uid, profile, months) => {
  if (role === "promoter") {
    const [history, perMonth] = await Promise.all([
      getIncomeHistoryForPromoter(uid).catch(() => []),
      Promise.all(months.map(m => Promise.all([
        getSalesByMonth(m, uid).catch(() => []),
        getTarget(uid, m).catch(() => null),
      ]))),
    ]);
    return {
      promoters: [{ ...profile, uid }], history,
      sales: perMonth.flatMap(([s]) => s), targets: perMonth.map(([, tg]) => tg).filter(Boolean),
    };
  }
  if (role === "supervisor") {
    // Rules chỉ cho đọc nhóm mình → query theo promoterId của nhóm (kể cả promoter đã lưu trữ)
    const ids = await getSupervisorAssignment(uid).catch(() => []);
    const [promoters, history, perMonth] = await Promise.all([
      getAssignedPromoters(uid, true).catch(() => []),
      getIncomeHistoryForPromoters(ids).catch(() => []),
      Promise.all(months.map(m => Promise.all([
        getSalesByMonthForPromoters(m, ids).catch(() => []),
        getTargetsForPromoters(m, ids).catch(() => []),
      ]))),
    ]);
    return { promoters, history, sales: perMonth.flatMap(([s]) => s), targets: perMonth.flatMap(([, tg]) => tg) };
  }
  const [promoters, history, perMonth] = await Promise.all([
    getAllPromoters(true),
    getIncomeHistoryAll().catch(() => []),
    Promise.all(months.map(m => Promise.all([getSalesByMonth(m).catch(() => []), getAllTargets(m).catch(() => [])]))),
  ]);
  return { promoters, history, sales: perMonth.flatMap(([s]) => s), targets: perMonth.flatMap(([, tg]) => tg) };
};

// Bảng màu phân loại (thứ tự cố định, đã kiểm tra mù màu) — màu đi theo phân khúc, không theo thứ hạng
const SERIES_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#6250d6"];
const OTHER_COLOR = "#a3a29c";
const MAX_SLOTS = SERIES_COLORS.length;

const norm = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");
const ml = (m) => `${m.slice(5, 7)}/${m.slice(2, 4)}`;   // "2026-10" → "10/26"
const pct = (sold, target) => (target > 0 ? Math.round((sold / target) * 100) : null);
const hitCls = (p) => (p === null ? "badge-gray" : p >= 100 ? "badge-green" : p >= 80 ? "badge-amber" : "badge-red");

export default function SalesReportPage({ role = "admin" }) {
  const { t } = useLanguage();
  const { user, profile } = useAuth();
  const uid = user?.uid;
  const isPromoter = role === "promoter";
  const thisMonth = format(new Date(), "yyyy-MM");
  const [from, setFrom] = useState(format(subMonths(new Date(), 5), "yyyy-MM"));
  const [to, setTo] = useState(thisMonth);
  const [promoterFilter, setPromoterFilter] = useState("");
  const [dim, setDim] = useState("segment");        // segment | series
  const [tab, setTab] = useState("promoter");       // promoter | model | mix
  const [mixMonth, setMixMonth] = useState("all");
  const [modelQuery, setModelQuery] = useState("");
  const [openPromoter, setOpenPromoter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ months: [], promoters: [], sales: [], targets: [], history: [], skus: [] });

  const months = useMemo(() => {
    if (!from || !to || from > to) return [];
    return eachMonthOfInterval({ start: parseISO(from + "-01"), end: parseISO(to + "-01") }).map(d => format(d, "yyyy-MM")).slice(-24);
  }, [from, to]);

  useEffect(() => {
    if (!months.length || !uid) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [scoped, skus] = await Promise.all([
        loadScoped(role, uid, profile, months),
        getAllSKUs().catch(() => []),
      ]);
      if (cancelled) return;
      // Chỉ promoter đang active: bỏ hẳn số liệu của người đã lưu trữ (kể cả đơn, target, số import)
      const activeIds = isPromoter ? null
        : new Set(scoped.promoters.filter(p => p.active !== false).map(p => p.uid));
      const keep = (x) => !activeIds || activeIds.has(x.promoterId);
      setData({
        months, skus,
        promoters: activeIds ? scoped.promoters.filter(p => activeIds.has(p.uid)) : scoped.promoters,
        history: scoped.history.filter(h => months.includes(h.month) && keep(h)),
        sales: scoped.sales.filter(keep), targets: scoped.targets.filter(keep),
      });
      setMixMonth(m => (m === "all" || months.includes(m) ? m : "all"));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [months, role, uid, profile, isPromoter]);

  // ── Gom số liệu ──
  const R = useMemo(() => {
    const { sales, targets, history, skus, promoters } = data;
    const skuBy = {};
    skus.forEach(s => { if (s.model) skuBy[norm(s.model)] = s; });
    const catOf = (model) => {
      const s = skuBy[norm(model)];
      return (dim === "series" ? s?.series : s?.segment) || t("rpt.unknown");
    };
    const segOfModel = (model) => skuBy[norm(model)]?.segment || "";

    const names = {};
    promoters.forEach(p => { names[p.uid] = { name: p.name, shop: p.shopName, archived: p.active === false }; });
    sales.forEach(s => { if (!names[s.promoterId]) names[s.promoterId] = { name: s.promoterName || s.promoterId, shop: "" }; });

    // promoter × tháng
    const cell = {};   // `${uid}|${m}` → { sold, he, target, heTarget, imported }
    const get = (uid, m) => (cell[`${uid}|${m}`] ||= { sold: 0, he: 0, target: 0, heTarget: 0, imported: false, models: {} });
    sales.forEach(s => {
      const c = get(s.promoterId, s.month);
      c.sold++; if (s.isHighend) c.he++;
      c.models[s.model] = (c.models[s.model] || 0) + 1;
    });
    targets.forEach(tg => {
      const c = get(tg.promoterId, tg.month);
      c.target = Number(tg.sellTarget) || 0; c.heTarget = Number(tg.highendTarget) || 0;
    });
    history.forEach(h => {   // tháng chưa có đơn trong app → dùng số import
      const c = get(h.promoterId, h.month);
      if (c.sold === 0 && (h.sold || h.highendSold)) {
        c.sold = Number(h.sold) || 0; c.he = Number(h.highendSold) || 0; c.imported = true;
        if (!names[h.promoterId]) names[h.promoterId] = { name: h.promoterName || h.promoterId, shop: "" };
      }
    });

    const uids = Object.keys(names).filter(uid => data.months.some(m => cell[`${uid}|${m}`]?.sold || cell[`${uid}|${m}`]?.target));
    const visibleUids = (promoterFilter ? uids.filter(u => u === promoterFilter) : uids)
      .sort((a, b) => (names[a].name || "").localeCompare(names[b].name || ""));

    const monthTotals = data.months.map(m => {
      let sold = 0, he = 0, target = 0, heTarget = 0;
      visibleUids.forEach(u => {
        const c = cell[`${u}|${m}`];
        if (c) { sold += c.sold; he += c.he; target += c.target; heTarget += c.heTarget; }
      });
      return { m, sold, he, target, heTarget };
    });

    // Đơn chi tiết (để chia theo model / phân khúc) — chỉ có với tháng có đơn trong app
    const vSales = sales.filter(s => !promoterFilter || s.promoterId === promoterFilter);

    // Model × tháng
    const modelRows = {};
    vSales.forEach(s => {
      const r = (modelRows[s.model] ||= { model: s.model, seg: segOfModel(s.model), series: skuBy[norm(s.model)]?.series || "", he: !!s.isHighend, byMonth: {}, total: 0, promoters: new Set() });
      r.byMonth[s.month] = (r.byMonth[s.month] || 0) + 1; r.total++; r.promoters.add(s.promoterId);
    });

    // Phân khúc: màu theo tổng cả kỳ của mọi promoter (không đổi khi lọc)
    const catTotals = {};
    sales.forEach(s => { const k = catOf(s.model); catTotals[k] = (catTotals[k] || 0) + 1; });
    const ranked = Object.keys(catTotals).sort((a, b) => catTotals[b] - catTotals[a]);
    const top = ranked.slice(0, ranked.length > MAX_SLOTS ? MAX_SLOTS - 1 : MAX_SLOTS);
    const OTHER = t("rpt.other");
    const colorOf = {};
    top.forEach((k, i) => { colorOf[k] = SERIES_COLORS[i]; });
    const bucket = (k) => (colorOf[k] ? k : OTHER);
    const cats = [...top, ...(ranked.length > top.length ? [OTHER] : [])];
    colorOf[OTHER] = OTHER_COLOR;

    const mixSales = vSales.filter(s => mixMonth === "all" || s.month === mixMonth);
    const mix = {};   // uid → { cat → n, total }
    mixSales.forEach(s => {
      const r = (mix[s.promoterId] ||= { total: 0 });
      const k = bucket(catOf(s.model));
      r[k] = (r[k] || 0) + 1; r.total++;
    });
    const mixTotal = { total: mixSales.length };
    mixSales.forEach(s => { const k = bucket(catOf(s.model)); mixTotal[k] = (mixTotal[k] || 0) + 1; });

    return { names, cell, visibleUids, allUids: uids, monthTotals, modelRows: Object.values(modelRows), cats, colorOf, mix, mixTotal, catOf, bucket };
  }, [data, dim, promoterFilter, mixMonth, t]);

  const sum = (arr, k) => arr.reduce((a, x) => a + (x[k] || 0), 0);
  const range = { sold: sum(R.monthTotals, "sold"), he: sum(R.monthTotals, "he"), target: sum(R.monthTotals, "target"), heTarget: sum(R.monthTotals, "heTarget") };
  const maxBar = Math.max(1, ...R.monthTotals.map(x => Math.max(x.sold, x.target)));

  const Tile = ({ label, value, sub }) => (
    <div className="card" style={{ flex: "1 1 150px", padding: "12px 14px", marginBottom: 0 }}>
      <div style={{ fontSize: 12, color: "#777" }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#888" }}>{sub}</div>}
    </div>
  );

  const HitBadge = ({ sold, target }) => {
    const p = pct(sold, target);
    return <span className={`badge ${hitCls(p)}`} style={{ fontSize: 10, padding: "0 5px" }}>{p === null ? "—" : `${p}%`}</span>;
  };

  // Thanh cơ cấu phân khúc (stacked, có khe 2px giữa các đoạn)
  const MixBar = ({ row }) => (
    <div style={{ display: "flex", gap: 2, height: 14, minWidth: 120 }}>
      {R.cats.filter(k => row[k]).map(k => (
        <div key={k} title={`${k}: ${row[k]} (${Math.round(row[k] / row.total * 100)}%)`}
          style={{ flex: row[k], background: R.colorOf[k], borderRadius: 3 }} />
      ))}
    </div>
  );

  const promoterList = R.allUids.slice().sort((a, b) => (R.names[a].name || "").localeCompare(R.names[b].name || ""));
  const modelRows = R.modelRows
    .filter(r => !modelQuery || norm(r.model).includes(norm(modelQuery)) || norm(r.seg).includes(norm(modelQuery)))
    .sort((a, b) => b.total - a.total);

  return (
    <>
      <div className="page-header">
        <div><h1>{t("rpt.title")}</h1><p>{t("rpt.subtitle")}</p></div>
      </div>

      {/* Bộ lọc — một hàng phía trên biểu đồ */}
      <div className="card" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", padding: "10px 14px" }}>
        <div><div className="form-label">{t("rpt.from")}</div><input type="month" value={from} max={to} onChange={e => setFrom(e.target.value)} /></div>
        <div><div className="form-label">{t("rpt.to")}</div><input type="month" value={to} min={from} onChange={e => setTo(e.target.value)} /></div>
        <div style={{ display: "flex", gap: 4 }}>
          {[3, 6, 12].map(n => (
            <button key={n} className="btn btn-sm" onClick={() => { setTo(thisMonth); setFrom(format(subMonths(new Date(), n - 1), "yyyy-MM")); }}>
              {t("rpt.last_n", { n })}
            </button>
          ))}
        </div>
        {!isPromoter && (
          <div style={{ minWidth: 180 }}>
            <div className="form-label">Promoter</div>
            <select value={promoterFilter} onChange={e => setPromoterFilter(e.target.value)}>
              <option value="">{t("rpt.all_promoters")}</option>
              {promoterList.map(u => <option key={u} value={u}>{R.names[u].name}</option>)}
            </select>
          </div>
        )}
      </div>

      {loading ? <div style={{ color: "#888", padding: 20 }}>{t("common.loading")}</div> : (
        <>
          {/* Tổng kỳ */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <Tile label={t("rpt.units")} value={range.sold.toLocaleString("vi-VN")} sub={`${months.length} ${t("rpt.months")}`} />
            <Tile label={t("rpt.vs_target")} value={pct(range.sold, range.target) === null ? "—" : `${pct(range.sold, range.target)}%`}
              sub={`${range.sold} / ${range.target} ${t("home.units")}`} />
            <Tile label={t("rpt.highend")} value={range.he.toLocaleString("vi-VN")}
              sub={range.heTarget ? `${pct(range.he, range.heTarget)}% · ${range.he} / ${range.heTarget}` : ""} />
            {!isPromoter && <Tile label={t("rpt.active_promoters")} value={R.visibleUids.length} />}
          </div>

          {/* Xu hướng theo tháng: cột = đã bán, gạch ngang = target */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 4 }}>{t("rpt.trend")}</div>
            <div style={{ display: "flex", gap: 14, fontSize: 11, color: "#666", marginBottom: 8 }}>
              <span><span style={{ display: "inline-block", width: 10, height: 10, background: SERIES_COLORS[0], borderRadius: 2, marginRight: 4 }} />{t("rpt.sold")}</span>
              <span><span style={{ display: "inline-block", width: 14, borderTop: "2px dashed #52514e", marginRight: 4, verticalAlign: "middle" }} />Target</span>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 170, borderBottom: "1px solid #e5e5e2", overflowX: "auto" }}>
              {R.monthTotals.map(x => {
                const h = (x.sold / maxBar) * 140, th = (x.target / maxBar) * 140;
                const p = pct(x.sold, x.target);
                return (
                  <div key={x.m} title={`${ml(x.m)}: ${x.sold} ${t("home.units")}${x.target ? ` / target ${x.target} (${p}%)` : ""} · HE ${x.he}`}
                    style={{ flex: "1 0 34px", position: "relative", height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", cursor: "default" }}>
                    <div style={{ fontSize: 11, color: "#333", marginBottom: 2 }}>{x.sold || ""}</div>
                    <div style={{ width: "60%", maxWidth: 36, height: Math.max(h, x.sold ? 2 : 0), background: SERIES_COLORS[0], borderRadius: "4px 4px 0 0" }} />
                    {x.target > 0 && (
                      <div style={{ position: "absolute", bottom: th, width: "80%", maxWidth: 46, borderTop: "2px dashed #52514e" }} />
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {R.monthTotals.map(x => (
                <div key={x.m} style={{ flex: "1 0 34px", textAlign: "center", fontSize: 11, color: "#777", paddingTop: 4 }}>
                  {ml(x.m)}
                  {x.target > 0 && <div><HitBadge sold={x.sold} target={x.target} /></div>}
                </div>
              ))}
            </div>
          </div>

          <div className="tabs" style={{ marginBottom: 12 }}>
            {[["promoter", t("rpt.tab_promoter")], ["model", t("rpt.tab_model")], ["mix", t("rpt.tab_mix")]].map(([k, l]) => (
              <div key={k} className={"tab" + (tab === k ? " active" : "")} onClick={() => setTab(k)}>{l}</div>
            ))}
          </div>

          {/* ── Promoter × tháng ── */}
          {tab === "promoter" && (
            <div className="card" style={{ overflowX: "auto" }}>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>{t("rpt.promoter_hint")}</div>
              <table style={{ fontSize: 12, borderCollapse: "collapse", minWidth: 600 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", position: "sticky", left: 0, background: "#fff", zIndex: 1 }}>Promoter</th>
                    {months.map(m => <th key={m} style={{ textAlign: "center", whiteSpace: "nowrap" }}>{ml(m)}</th>)}
                    <th style={{ textAlign: "center" }}>{t("rpt.total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {R.visibleUids.map(u => {
                    const tot = months.reduce((a, m) => { const c = R.cell[`${u}|${m}`]; if (c) { a.sold += c.sold; a.target += c.target; a.he += c.he; } return a; }, { sold: 0, target: 0, he: 0 });
                    const open = openPromoter === u;
                    return [
                      <tr key={u} style={{ borderTop: "1px solid #f0f0f0", cursor: "pointer", background: open ? "#FFF7F7" : undefined }}
                        onClick={() => setOpenPromoter(open ? null : u)}>
                        <td style={{ position: "sticky", left: 0, background: open ? "#FFF7F7" : "#fff", padding: "6px 8px 6px 0", whiteSpace: "nowrap" }}>
                          <span style={{ color: "#999" }}>{open ? "▾" : "▸"}</span> <b>{R.names[u].name}</b>
                          <div style={{ fontSize: 10, color: "#999" }}>{R.names[u].shop}</div>
                        </td>
                        {months.map(m => {
                          const c = R.cell[`${u}|${m}`];
                          if (!c || (!c.sold && !c.target)) return <td key={m} style={{ textAlign: "center", color: "#ccc" }}>—</td>;
                          return (
                            <td key={m} style={{ textAlign: "center", padding: "4px 6px", whiteSpace: "nowrap" }}
                              title={`${t("rpt.sold")} ${c.sold}${c.target ? ` / target ${c.target}` : ""} · HE ${c.he}${c.heTarget ? ` / ${c.heTarget}` : ""}${c.imported ? ` · ${t("rpt.imported")}` : ""}`}>
                              <div style={{ fontStyle: c.imported ? "italic" : "normal" }}>
                                <b>{c.sold}</b><span style={{ color: "#999" }}>{c.target ? `/${c.target}` : ""}</span>
                              </div>
                              {c.target > 0 && <HitBadge sold={c.sold} target={c.target} />}
                              <div style={{ fontSize: 10, color: "#888" }}>HE {c.he}{c.heTarget ? `/${c.heTarget}` : ""}</div>
                            </td>
                          );
                        })}
                        <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                          <b>{tot.sold}</b><span style={{ color: "#999" }}>{tot.target ? `/${tot.target}` : ""}</span>
                          {tot.target > 0 && <div><HitBadge sold={tot.sold} target={tot.target} /></div>}
                        </td>
                      </tr>,
                      open && (
                        <tr key={u + "_d"}>
                          <td colSpan={months.length + 2} style={{ background: "#FFFBFB", padding: "8px 10px" }}>
                            <PromoterDetail u={u} R={R} months={months} sales={data.sales} t={t} MixBar={MixBar} />
                          </td>
                        </tr>
                      ),
                    ];
                  })}
                  <tr style={{ borderTop: "2px solid #ddd", fontWeight: 600 }}>
                    <td style={{ position: "sticky", left: 0, background: "#fff" }}>{t("rpt.total")}</td>
                    {R.monthTotals.map(x => (
                      <td key={x.m} style={{ textAlign: "center" }}>
                        {x.sold}<span style={{ color: "#999", fontWeight: 400 }}>{x.target ? `/${x.target}` : ""}</span>
                        {x.target > 0 && <div><HitBadge sold={x.sold} target={x.target} /></div>}
                      </td>
                    ))}
                    <td style={{ textAlign: "center" }}>{range.sold}<span style={{ color: "#999", fontWeight: 400 }}>{range.target ? `/${range.target}` : ""}</span></td>
                  </tr>
                </tbody>
              </table>
              <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#777", marginTop: 8, flexWrap: "wrap" }}>
                <span><span className="badge badge-green" style={{ fontSize: 10 }}>≥100%</span> {t("rpt.hit")}</span>
                <span><span className="badge badge-amber" style={{ fontSize: 10 }}>80–99%</span> {t("rpt.near")}</span>
                <span><span className="badge badge-red" style={{ fontSize: 10 }}>&lt;80%</span> {t("rpt.miss")}</span>
                <span style={{ fontStyle: "italic" }}>{t("rpt.imported_note")}</span>
              </div>
            </div>
          )}

          {/* ── Model × tháng ── */}
          {tab === "model" && (
            <div className="card" style={{ overflowX: "auto" }}>
              <input value={modelQuery} onChange={e => setModelQuery(e.target.value)} placeholder={t("rpt.model_search")}
                style={{ maxWidth: 320, marginBottom: 10 }} />
              <table style={{ fontSize: 12, borderCollapse: "collapse", minWidth: 600 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Model</th>
                    <th style={{ textAlign: "left" }}>{t("rpt.segment")}</th>
                    {months.map(m => <th key={m} style={{ textAlign: "center" }}>{ml(m)}</th>)}
                    <th style={{ textAlign: "center" }}>{t("rpt.total")}</th>
                    {!isPromoter && <th style={{ textAlign: "center" }}>Promoter</th>}
                  </tr>
                </thead>
                <tbody>
                  {modelRows.map(r => (
                    <tr key={r.model} style={{ borderTop: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "5px 8px 5px 0" }}>{r.model}{r.he && <span style={{ color: "#C8152A" }}> ★</span>}</td>
                      <td style={{ color: "#777", whiteSpace: "nowrap" }}>{r.seg || "—"}</td>
                      {months.map(m => <td key={m} style={{ textAlign: "center", color: r.byMonth[m] ? "#222" : "#ccc" }}>{r.byMonth[m] || "—"}</td>)}
                      <td style={{ textAlign: "center", fontWeight: 600 }}>{r.total}</td>
                      {!isPromoter && <td style={{ textAlign: "center", color: "#777" }}>{r.promoters.size}</td>}
                    </tr>
                  ))}
                  {modelRows.length === 0 && <tr><td colSpan={months.length + (isPromoter ? 3 : 4)} style={{ color: "#aaa", textAlign: "center", padding: 16 }}>{t("rpt.no_sales")}</td></tr>}
                </tbody>
              </table>
              <div style={{ fontSize: 11, color: "#888", marginTop: 8 }}>{t("rpt.model_note")}</div>
            </div>
          )}

          {/* ── Cơ cấu phân khúc theo promoter ── */}
          {tab === "mix" && (
            <div className="card" style={{ overflowX: "auto" }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10, alignItems: "flex-end" }}>
                <div>
                  <div className="form-label">{t("rpt.classify_by")}</div>
                  <select value={dim} onChange={e => setDim(e.target.value)}>
                    <option value="segment">{t("rpt.by_segment")}</option>
                    <option value="series">{t("rpt.by_series")}</option>
                  </select>
                </div>
                <div>
                  <div className="form-label">{t("x.month")}</div>
                  <select value={mixMonth} onChange={e => setMixMonth(e.target.value)}>
                    <option value="all">{t("rpt.whole_range")}</option>
                    {months.slice().reverse().map(m => <option key={m} value={m}>{ml(m)}</option>)}
                  </select>
                </div>
              </div>
              {/* Chú thích: màu luôn đi kèm tên */}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11, color: "#555", marginBottom: 10 }}>
                {R.cats.map(k => (
                  <span key={k}><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: R.colorOf[k], marginRight: 4 }} />{k}</span>
                ))}
              </div>
              <table style={{ fontSize: 12, borderCollapse: "collapse", minWidth: 600 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Promoter</th>
                    <th style={{ textAlign: "left", minWidth: 140 }}>{t("rpt.mix")}</th>
                    {R.cats.map(k => <th key={k} style={{ textAlign: "center", whiteSpace: "nowrap" }}>{k}</th>)}
                    <th style={{ textAlign: "center" }}>{t("rpt.total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {R.visibleUids.filter(u => R.mix[u]).map(u => {
                    const row = R.mix[u];
                    return (
                      <tr key={u} style={{ borderTop: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "6px 8px 6px 0", whiteSpace: "nowrap" }}><b>{R.names[u].name}</b></td>
                        <td><MixBar row={row} /></td>
                        {R.cats.map(k => (
                          <td key={k} style={{ textAlign: "center", color: row[k] ? "#222" : "#ccc" }}>
                            {row[k] ? <>{row[k]} <span style={{ color: "#999", fontSize: 10 }}>{Math.round(row[k] / row.total * 100)}%</span></> : "—"}
                          </td>
                        ))}
                        <td style={{ textAlign: "center", fontWeight: 600 }}>{row.total}</td>
                      </tr>
                    );
                  })}
                  {R.mixTotal.total > 0 && (
                    <tr style={{ borderTop: "2px solid #ddd", fontWeight: 600 }}>
                      <td>{t("rpt.total")}</td>
                      <td><MixBar row={R.mixTotal} /></td>
                      {R.cats.map(k => (
                        <td key={k} style={{ textAlign: "center" }}>
                          {R.mixTotal[k] || "—"} {R.mixTotal[k] ? <span style={{ color: "#999", fontSize: 10, fontWeight: 400 }}>{Math.round(R.mixTotal[k] / R.mixTotal.total * 100)}%</span> : null}
                        </td>
                      ))}
                      <td style={{ textAlign: "center" }}>{R.mixTotal.total}</td>
                    </tr>
                  )}
                  {R.mixTotal.total === 0 && <tr><td colSpan={R.cats.length + 3} style={{ color: "#aaa", textAlign: "center", padding: 16 }}>{t("rpt.no_sales")}</td></tr>}
                </tbody>
              </table>
              <div style={{ fontSize: 11, color: "#888", marginTop: 8 }}>{t("rpt.mix_note")}</div>
            </div>
          )}
        </>
      )}
    </>
  );
}

// Chi tiết một promoter: từng tháng (target, highend, model bán chạy) + cơ cấu phân khúc cả kỳ
function PromoterDetail({ u, R, months, sales, t, MixBar }) {
  const mine = sales.filter(s => s.promoterId === u);
  const mixRow = { total: 0 };
  mine.forEach(s => { const k = R.bucket(R.catOf(s.model)); mixRow[k] = (mixRow[k] || 0) + 1; mixRow.total++; });
  return (
    <div>
      <table style={{ fontSize: 12, borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr style={{ color: "#666" }}>
            <th style={{ textAlign: "left" }}>{t("x.month")}</th>
            <th style={{ textAlign: "center" }}>{t("rpt.sold")} / Target</th>
            <th style={{ textAlign: "center" }}>Highend / Target</th>
            <th style={{ textAlign: "left" }}>{t("rpt.top_models")}</th>
          </tr>
        </thead>
        <tbody>
          {months.slice().reverse().map(m => {
            const c = R.cell[`${u}|${m}`];
            if (!c || (!c.sold && !c.target)) return null;
            const top = Object.entries(c.models).sort((a, b) => b[1] - a[1]).slice(0, 3);
            const p = c.target > 0 ? Math.round(c.sold / c.target * 100) : null;
            const ph = c.heTarget > 0 ? Math.round(c.he / c.heTarget * 100) : null;
            return (
              <tr key={m} style={{ borderTop: "1px solid #f3eaea" }}>
                <td style={{ padding: "4px 8px 4px 0", whiteSpace: "nowrap" }}>{m.slice(5, 7)}/{m.slice(0, 4)}{c.imported && <span style={{ color: "#999", fontStyle: "italic" }}> · {t("rpt.imported")}</span>}</td>
                <td style={{ textAlign: "center" }}>{c.sold} / {c.target || "—"} {p !== null && <span className={`badge ${hitCls(p)}`} style={{ fontSize: 10 }}>{p}%</span>}</td>
                <td style={{ textAlign: "center" }}>{c.he} / {c.heTarget || "—"} {ph !== null && <span className={`badge ${hitCls(ph)}`} style={{ fontSize: 10 }}>{ph}%</span>}</td>
                <td style={{ color: "#555" }}>{top.length ? top.map(([mo, n]) => `${mo} (${n})`).join(", ") : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {mixRow.total > 0 && (
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, fontSize: 11, color: "#555", flexWrap: "wrap" }}>
          <span>{t("rpt.mix")}:</span>
          <div style={{ width: 220 }}><MixBar row={mixRow} /></div>
          {R.cats.filter(k => mixRow[k]).map(k => (
            <span key={k}><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: R.colorOf[k], marginRight: 3 }} />{k} {Math.round(mixRow[k] / mixRow.total * 100)}%</span>
          ))}
        </div>
      )}
    </div>
  );
}
